import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@practium/ui';
import {
	useNavigation,
	useAuthorizedRoutes,
	buildSidebarConfig,
} from '@practium/features/navigation';
import { useAuth } from '@practium/features/auth';
import { useForBinding, ForBindingBanner } from '@practium/features/forBinding';
import { usePermissions } from '@practium/features/permissions';
import { useClientNavbarConfig } from '../navbar/config/navbar.config';
import { UserHierarchyLevel } from '@practium/shared';
import { ROUTE_PERMISSION_MAP, FOR_BINDING_ROUTE_PERMISSION_MAP } from './routePermissionMap';

const OWNER_ONLY_PATHS = ['/', '/suporte', '/solicitacoes', '/meu-plano'];
const ALWAYS_VISIBLE_PATHS = ['/', '/flora-ai'];

interface DynamicLayoutProps {
	userLevel?: UserHierarchyLevel;
}

export const DynamicLayout = ({ userLevel }: DynamicLayoutProps) => {
	const navigate = useNavigate();
	const { logout } = useAuth();
	const { isForBinding } = useForBinding();
	const { abilities } = usePermissions();
	const currentLevel = userLevel ?? UserHierarchyLevel.Farmer;
	const { routes } = useAuthorizedRoutes(currentLevel);

	const handleLogout = useCallback(() => {
		const doLogout = async () => {
			await logout();
			navigate('/login', { replace: true });
		};
		doLogout();
	}, [logout, navigate]);

	const sidebarConfig = useMemo(() => {
		const permMap = isForBinding ? FOR_BINDING_ROUTE_PERMISSION_MAP : ROUTE_PERMISSION_MAP;

		const permittedRoutes = routes.filter((route) => {
			const subject = permMap[route.path];
			if (!subject) return ALWAYS_VISIBLE_PATHS.includes(route.path) || !isForBinding;
			return abilities.can('read', subject);
		});

		const visibleRoutes = isForBinding
			? permittedRoutes.filter((r) => !OWNER_ONLY_PATHS.includes(r.path))
			: permittedRoutes;

		const config = buildSidebarConfig(currentLevel, visibleRoutes);

		if (config.footerRoutes) {
			config.footerRoutes = config.footerRoutes.map((route) => {
				if (route.path === '/logout') {
					return { ...route, onClick: handleLogout };
				}
				return route;
			});
		}

		return config;
	}, [currentLevel, routes, abilities, handleLogout, isForBinding]);

	const navbarConfig = useClientNavbarConfig();

	const { isSidebarOpen, handleMenuClick, handleCloseSidebar, dynamicNavbarConfig } = useNavigation(
		{
			navbarConfig,
			customTitles: {
				'/analises': 'Análises',
				'/diario-de-campo': 'Diário de campo',
			},
		}
	);

	return (
		<AppLayout
			sidebarConfig={sidebarConfig}
			navbarConfig={dynamicNavbarConfig}
			isSidebarOpen={isSidebarOpen}
			onMenuClick={handleMenuClick}
			onCloseSidebar={handleCloseSidebar}
			topBanner={<ForBindingBanner />}
		/>
	);
};
